import ProductCard from "./ProductCard";
import Filter from "./Filter";
import Sort from "../../components/Sort";
import { useProductsQuery } from "../../components/useProductsQuery";
import "./Products.css";
import loadingPage from "../Status/Loading";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, useState } from "react";
import ScrollButton from "../../components/ScrollButton";

export default function Products() {
  const parentRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useProductsQuery();

  const allProducts = data?.pages.flatMap(page => page?.products ?? []) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  const min_width = 180;
  const gap = 16;

  const columns = containerWidth > 0
    ? Math.max(1, Math.floor((containerWidth + gap) / (min_width + gap)))
    : 5;

  const item_width = containerWidth > 0
    ? (containerWidth - (gap * (columns - 1))) / columns
    : 200;

  const item_height = item_width * 1.75;

  const rowCount = Math.ceil(allProducts.length / columns);

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    estimateSize: () => item_height + gap,
    getScrollElement: () => parentRef.current,
    overscan: 2,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: columns,
    getScrollElement: () => parentRef.current,
    estimateSize: () => item_width + gap,
    overscan: 2,
  });

  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setContainerWidth(parentRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (parentRef.current) {
      resizeObserver.observe(parentRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      rowVirtualizer.measure();
      columnVirtualizer.measure();
    });

    return () => cancelAnimationFrame(id);
  }, [item_width, item_height, columns, rowCount]);

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) return;

    if (lastItem.index >= rowCount - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    rowCount,
    isFetchingNextPage,
    rowVirtualizer.getVirtualItems(),
  ]);

  if (isLoading) {
    return loadingPage();
  }

  if (!data) {
    return <main className="products"><p>No products found</p></main>;
  }

  return (
    <main className="products">
      <div className="products-layout">
        <Filter totalProducts={total} />

        <div className="products-content">
          <Sort />
          <ScrollButton scrollContainerRef={parentRef} />
          <div
            ref={parentRef}
            style={{
              height: '80vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              width: '100%',
            }}
          >
            <div
              style={{
                width: '100%',
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) =>
                columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                  const itemIndex = virtualRow.index * columns + virtualColumn.index;

                  if (itemIndex >= allProducts.length) {
                    return null;
                  }

                  const product = allProducts[itemIndex];
                  if (!product) {
                    return null;
                  }

                  return (
                    <div
                      key={itemIndex}
                      className="product-card-wrapper"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${item_width}px`,
                        height: `${item_height}px`,
                        transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{
                        padding: `${gap / 2}px`,
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          overflow: 'hidden',
                        }}>
                          <ProductCard key={product.id} {...product} />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {isFetchingNextPage && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                Loading more...
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}